import type { TFunction } from 'i18next'

/** Backend / Nest-style JSON: { "message": "...", "statusCode": 401 } */
function extractMessageFromJson(text: string): string | null {
  try {
    const j = JSON.parse(text.trim()) as { message?: unknown }
    if (typeof j.message === 'string' && j.message.trim()) return j.message.trim()
    if (Array.isArray(j.message) && j.message.every((x) => typeof x === 'string')) {
      return (j.message as string[]).join(', ')
    }
  } catch {
    /* plain text */
  }
  return null
}

function mapKnownEnglishMessage(message: string, t: TFunction): string | null {
  const m = message.toLowerCase()
  if (m.includes('invalid credentials')) return t('auth.errors.invalidCredentials')
  if (m.includes('user profile not found')) return t('auth.errors.profileNotFound')
  if (m.includes('invalid refresh token')) return t('auth.errors.invalidRefreshToken')
  if (m === 'invalid token' || (m.includes('invalid token') && !m.includes('refresh')))
    return t('auth.errors.invalidSessionToken')
  if (m.includes('invalid user role')) return t('auth.errors.invalidUserRole')
  if (m.includes('token is required')) return t('auth.errors.tokenRequired')
  if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('load failed'))
    return t('auth.errors.networkError')
  return null
}

/** Map register/login/refresh response body (JSON or plain) to a user-facing string. */
export function translateAuthResponseBody(errorText: string, t: TFunction): string {
  const trimmed = errorText.trim()
  if (!trimmed) return t('auth.errors.requestFailed')

  const fromJson = extractMessageFromJson(trimmed)
  const message = fromJson ?? trimmed

  const mapped = mapKnownEnglishMessage(message, t)
  if (mapped) return mapped

  return t('auth.errors.serverReturned', { message })
}

export function formatRefreshFailureReason(raw: string, t: TFunction): string {
  const trimmed = raw.trim()
  if (!trimmed) return t('auth.errors.unknownError')
  if (trimmed === 'Refresh succeeded but returned no backend token.') {
    return t('auth.errors.refreshNoToken')
  }
  if (trimmed === 'Unknown refresh error') {
    return t('auth.errors.unknownError')
  }
  return translateAuthResponseBody(trimmed, t)
}

/** Map Supabase Auth errors (messages are usually English). */
export function translateSupabaseAuthError(
  err: { message?: string; code?: string; status?: number } | null | undefined,
  t: TFunction
): string {
  if (!err?.message?.trim()) return t('auth.errors.authenticationFailed')

  const code = (err.code || '').toLowerCase()
  const msg = err.message.trim()
  const lower = msg.toLowerCase()

  if (code === 'invalid_credentials' || lower.includes('invalid login credentials')) {
    return t('auth.errors.invalidCredentials')
  }
  if (
    code === 'email_not_confirmed' ||
    lower.includes('email not confirmed') ||
    lower.includes('not confirmed')
  ) {
    return t('auth.errors.emailNotConfirmed')
  }
  if (
    code === 'user_already_exists' ||
    lower.includes('user already registered') ||
    lower.includes('already been registered')
  ) {
    return t('auth.errors.userAlreadyRegistered')
  }
  if (
    code === 'weak_password' ||
    lower.includes('weak password') ||
    lower.includes('password is too weak')
  ) {
    return t('auth.errors.weakPassword')
  }
  if (
    code === 'over_request_rate_limit' ||
    lower.includes('rate limit') ||
    lower.includes('too many requests') ||
    err.status === 429
  ) {
    return t('auth.errors.tooManyAttempts')
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return t('auth.errors.networkError')
  }

  const mapped = mapKnownEnglishMessage(msg, t)
  if (mapped) return mapped

  return t('auth.errors.signInProviderMessage', { message: msg })
}

export function translateAuthCaughtError(err: unknown, t: TFunction): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as Error).message === 'string') {
    const m = (err as Error).message.trim()
    if (m) return t('auth.errors.signInProviderMessage', { message: m })
  }
  return t('auth.unexpectedError')
}
